<template>
    <div class="lp-google-map" :id="`lp-map-${inputId}`" aria-hidden="true"></div>
</template>

<script lang="ts">
    import { Loader } from '@googlemaps/js-api-loader';
    import { v4 as uuid } from 'uuid';
    import { ICenter, ILocationMarker } from "../helpers/LocationFinderTypes"
    import { onMounted, reactive, ref, watch, defineEmits } from 'vue';

    export default {
        name: 'GoogleMap',
        props: {
            apiKey: {
                type: String,
                required: true,
            },
            center: {
                type: Object,
                required: true,
            },
            initialMarker: {
                type: Boolean,
                required: false,
                default: true,
            },
            markers: {
                type: Array,
                required: false,
                default: () => [],
            },
            zipCode: {
                type: String,
                required: false
            },
            zoom: {
                type: Number,
                required: false,
                default: 17,
                validator: (value) => {
                    return value >= 0 && value <= 21;
                },
            },
            fitMapToLocations: {
                type: Boolean,
                required: false,
                default: false
            },
            mapBounds: {
                type: Object,
                required: false,
                default: null
            },
            detailsUrl: {
              type: String,
              required: true
            },
            filterByDistanceFlag: {
              type: Boolean,
              required: false,
              default: false
            }
        },
        setup(props, { emit }) {
            const inputId = ref(uuid());

            // MAP
            let map: google.maps.Map;

            const loader = new Loader({
                apiKey: props.apiKey,
                version: 'weekly',
                libraries: ['places'],
            });

            const mapOptions = reactive({
                center: props.center,
                disableDefaultUI: true,
                zoom: props.zoom,
                zoomControl: true,
                scaleControl: true
            });

            const initMap = () => {
                loader
                    .load()
                    .then(() => {
                        return new google.maps.Map(
                            document.getElementById(`lp-map-${inputId.value}`),
                            mapOptions
                        );
                    })
                    .then((gmap) => {
                        map = gmap;

                        map.addListener("center_changed", function () {
                            const center: ICenter = map.getCenter().toJSON()
                            if (!props.filterByDistanceFlag) {
                              emit("new-center", center)
                            }
                        })

                        initInfoWindow();

                        if (props.initialMarker) {
                            createInitialMarker({ position: props.center });
                        }

                        if (props.markers.length > 0) {
                            updateMarkers();
                        }

                    })
                    .catch((e) => {
                        console.error(e);
                    });
            };

            const fitMapToMarkers = () => {
                if (map && props.fitMapToLocations) {
                    if (liveMarkers.length > 0) {
                        const mapBounds = new google.maps.LatLngBounds();
                        for (var i = 0; i < liveMarkers.length; i++) {
                            mapBounds.extend(liveMarkers[i].position);
                        }

                        map.fitBounds(mapBounds);

                        if (map.getZoom() > props.zoom) {
                            map.setZoom(props.zoom);
                        }
                    }
                }
            };

            const moveToLocation = () => {
                if (map) {
                    const center = new google.maps.LatLng(props.center);
                    map.panTo(center);

                    if (props.FitMapToLocation) {
                        map.setZoom(10);
                    }
                }
            };

            watch(() => props.center,
              (newVal, oldVal) => {
                moveToLocation();
              }  
            );

            // MARKERS
            let liveMarkers = [];
            let infoWindow: google.maps.InfoWindow;

            const createInitialMarker = (marker) => {
                const newMarker = new google.maps.Marker({
                    position: marker.position,
                    optimized: false,
                    map,
                });
                liveMarkers.push(newMarker);
            };

            const createMarker = (marker: ILocationMarker) => {
                const markerIcon = createMarkerIcon(marker.PinBackground, marker.PinBorder, marker.Base64PinIcon);

                const newMarker = new google.maps.Marker({
                    position: { lat: marker.Latitude as number, lng: marker.Longitude as number },
                    title: marker.Title ? marker.Title : '',
                    optimized: false,
                    map,
                    icon: markerIcon
                });

                newMarker.set("id", marker.Id);

                newMarker.addListener('click', () => openInfoWindow(newMarker, marker));
                liveMarkers.push(newMarker);
            };

            const updateMarkers = () => {
                const liveMarkersIds: string[] = liveMarkers.map(elm => elm.id);
                const newMarkersIds: string[] = props.markers.map(elm => elm.Id);

                const markersIdToRemove = liveMarkersIds.filter(elm => !newMarkersIds.some(item => item == elm));
                const markersIdToAppend = newMarkersIds.filter(elm => !liveMarkersIds.some(item => item == elm));

                liveMarkers.forEach((marker, index) => {
                    if (markersIdToRemove.indexOf(marker.id) != -1) {
                      marker.setMap(null);
                      fitMapToMarkers();
                    }
                });

                (props.markers as ILocationMarker[]).forEach((marker, index) => {
                    if (marker.Latitude && marker.Longitude && markersIdToAppend.some(item => item == marker.Id)) {
                      createMarker(marker);
                      fitMapToMarkers();
                    }
                });

                liveMarkers = liveMarkers.filter(elm => !markersIdToRemove.some(item => item == elm.id));
            };

            //Here we're creating the icon dinamically, with a custom color and border for each location.
            //In order to add an incon on top, we should add an <image> tag and pass the icon on BASE64 using the same snippet data:image/png+xml;utf-8, <ICON>
            const createMarkerIcon = (pinColor, borderColor, pinIcon) => {
                return {
                    anchor: new google.maps.Point(36, 48),
                  url: 'data:image/svg+xml;utf-8, \
                        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="48" viewBox="0 0 36 48"> \
	                        <g transform="translate(0 0)"> \
		                        <path d="M17.958,48a.941.941,0,0,1-.769-.4L4.55,30.281c-.22-.316-.458-.628-.687-.929l-.234-.309c-.271-.359-.538-.753-.866-1.278l-.338-.539A18.239,18.239,0,0,1,5.272,5.311,17.816,17.816,0,0,1,18,0c.584,0,1.177.028,1.763.085A18.172,18.172,0,0,1,33.3,27.679l-.314.493c-.3.453-.617.888-.941,1.293l-.18.226,0,.006c-.266.333-.541.676-.8,1.025L18.7,47.629A.932.932,0,0,1,17.958,48ZM18,3.2A14.9,14.9,0,0,0,3.177,18.133a14.9,14.9,0,0,0,2,7.485l.43.67c.236.369.493.735.762,1.09.227.3.51.668.783,1.058L17.986,43.249,29.017,28.13l.131-.162a14.987,14.987,0,0,0,1.2-1.57l.258-.407A14.96,14.96,0,0,0,18.938,3.23C18.636,3.211,18.321,3.2,18,3.2Z" fill="' + encodeURIComponent(borderColor) + '"/> \
		                        <path d="M14.823,0c.311,0,.626.01.938.029A14.96,14.96,0,0,1,27.426,22.79l-.258.407a15.123,15.123,0,0,1-1.2,1.57l-.131.161L14.809,40.048,3.971,25.237c-.264-.378-.527-.724-.783-1.059h0q-.4-.529-.762-1.09L2,22.418H2A14.894,14.894,0,0,1,.569,19.048,15.054,15.054,0,0,1,0,14.932,14.9,14.9,0,0,1,14.823,0Z" transform="translate(3.177 3.201)" fill="' + encodeURIComponent(pinColor) + '"/> \
	                        </g> \
	                        <image href="'+ pinIcon + '" width="24" height="30" x="6" y="6" /> \
                        </svg>',
                    scaledSize: new google.maps.Size(54, 54)
                }
            };

            const url = props.detailsUrl + "/location-details/";

            const createInfoContent = (info: ILocationMarker) => {
                const fullUrl = url + info.Title;
                return `
                     <div id="content" class="lp-map-info-window">
                        <div class="row">
                          ${fullUrl ? `<a class="info-title h4" href="${fullUrl}">${info.DisplayName}</a>` : `<h4 class="info-title">${info.DisplayName || ""}</h4>`}
                        </div>
                        <div class="info-body row">
                          <div class="col-12 col-md-6">
                            <div>
                              <div class="mb-5">
                                <ul class="contact">
                                  ${info.Street ? `<li class="p my-0">${info.Street}</li>` : ""}

                                  ${info.City || info.State || info.Zip ?
                                                    `<li class="p my-0"> ${info.City || ""} ${info.State || ""} ${info.Zip || ""} </li>` : ""}

                                  ${info.Phone ? `<li class="p my-0"><a href="tel:${info.Phone.replace(/[^0-9]/g, '')}">${info.Phone}</a> </li>` : ``}
                                </ul>
                              </div>
                              <div>
                                ${info.Hours}
                              </div>
                            </div>
                          </div>
                          <div class="col-12 col-md-6">
                            ${info.CustomFilters.length > 0 ? `<ul class="tags">` : ``}
                            ${info.CustomFilters.map((tag) => {
                                return `<li class="my-0">${tag.Title}</li>`
                              }).join('')
                            }
                            ${info.CustomFilters.length > 0 ? `</ul>` : ``}
                          </div>
                        </div>
                    </div>
                    `
            }

            const initInfoWindow = () => {
                infoWindow = new google.maps.InfoWindow({ content: 'wat' });
            };

            const openInfoWindow = (marker, info) => {
                if (infoWindow) {
                    infoWindow.setContent(createInfoContent(info));
                    infoWindow.open(map, marker);
                }
            };

            watch(
                () => props.markers,
                () => updateMarkers()
            );

            //watch(props.mapBounds, (bounds: google.maps.LatLngBounds) => fitMapToMarkers(bounds));

            onMounted(() => {
                initMap();
            });

            return { inputId };
        },
    };
</script>

<style lang="scss" scoped></style>
